import { GroupService } from './../../../core/services/group.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { combineLatest, EMPTY, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { IdleService } from './../../../core/services/idle.service';
import { UserService } from './../../../core/services/user.service';
import { FeedbackService } from './../../../core/services/feedback.service';
import { TaskService } from './../../../core/services/task.service';
import { isTaskForUser } from './../../../core/utils/task-assignees';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { Announcement, AnnouncementService } from './../../../core/services/announcement.service';
import { AnnouncementGateService } from './../../../core/services/announcement-gate.service';
import { AnnouncementDialogComponent } from '../announcement-dialog/announcement-dialog.component';
import { environment } from './../../../../environments/environment';

@Component({
  selector: 'app-custom-snackbar',
  template: `
    <div class="snackbar-content">
      <span>Email verification is required. Please ensure your email is verified within the next 24 hours to avoid disruptions.</span>
      <div class="snackbar-actions">
        <button mat-button (click)="resend()">Resend</button>
        <button mat-button (click)="dismiss()">Dismiss</button>
      </div>
    </div>
  `,
  styles: [`
    .snackbar-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .snackbar-actions {
      display: flex;
      gap: 10px;
    }
  `]
})
export class EmailVerificationSnackbarComponent {
  constructor(private snackBarRef: MatSnackBarRef<EmailVerificationSnackbarComponent>) { }

  resend() {
    this.snackBarRef.dismissWithAction();
  }

  dismiss() {
    this.snackBarRef.dismiss();
  }
}


@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  isMobile: boolean = false;
  user?: any;
  private unsubscribe$ = new Subject<void>();
  private handleBeforeUnload?: (event: BeforeUnloadEvent) => string | undefined;
  private dialogShown = false;

  /**
   * One announcement dialog per session, maximum.
   *
   * Held in memory rather than `localStorage` deliberately. The DURABLE state is the receipt on the
   * server — a user on two devices should not be shown the same release note twice — and
   * `localStorage.clear()` already runs on logout, so a flag there would be forgotten at exactly
   * the moment it is least wanted.
   */
  private announcementDialogShown = false;

  /** Whether the deferred retry (F4) has already been armed, so it arms at most once. */
  private announcementRetryArmed = false;

  // Computed per access, not stored. A field initialiser would freeze the date at
  // construction, so a tab left open past midnight would keep reading (and writing)
  // the previous day's dismissal key and never prompt again.
  private get tasksDismissedKey(): string {
    return `${moment().format('YYYY-MM-DD')}-pending-tasks-dismissed`;
  }

  constructor(
    private breakpointObserver: BreakpointObserver,
    private snackBar: MatSnackBar,
    private userService: UserService,
    private taskService: TaskService,
    private feedbackService: FeedbackService,
    private groupService: GroupService,
    private router: Router,
    private idle: IdleService,
    private dialog: MatDialog,
    private announcementService: AnnouncementService,
    private announcementGate: AnnouncementGateService,
  ) {
  }

  ngOnInit() {
    this.observeBreakpoints();
    this.subscribeToUserProfile();
    this.setupBeforeUnloadWarning();
    this.checkPendingTasksForToday();
    this.checkShouldEnableExperimentalFeatures();
    this.watchAnnouncements();
  }

  ngOnDestroy() {
    // Remove beforeunload event listener
    if (this.handleBeforeUnload) {
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Opens the announcement dialog once per session, when it is safe to interrupt.
   *
   * `tasks/announcements.md` — Phase F4.
   *
   * Waits for BOTH the user profile and the selected group, because the audience is evaluated
   * against the user's role in the group they are working in — firing before the group resolves
   * would show them only the announcements addressed to everyone and then never ask again.
   *
   * If the gate refuses (recording, another dialog open, or off the landing route) nothing is
   * retried on a timer. It arms ONCE on the next navigation that lands somewhere allowed. A poll
   * would eventually find a gap between two clinical tasks and open a dialog into it, which is
   * precisely the behaviour the gate exists to prevent.
   */
  private watchAnnouncements() {
    combineLatest([
      this.userService.userProfile$,
      this.groupService.selectedGroup$,
      this.announcementService.dialog$,
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([user, group, pending]) => {
        if (!user || !group || !pending?.length) {
          return;
        }
        if (this.announcementDialogShown) {
          return;
        }

        // `critical` is the one type allowed to interrupt a clinician mid-task, so it is asked of
        // the gate separately and bypasses every rule.
        const critical = pending.filter(a => a.requiresAcknowledgement);

        if (critical.length) {
          this.openAnnouncementDialog(critical, true);
          return;
        }

        if (this.announcementGate.canInterrupt()) {
          // A short settle delay so the dialog does not race the first paint of the schedule.
          setTimeout(() => {
            if (!this.announcementDialogShown && this.announcementGate.canInterrupt()) {
              this.openAnnouncementDialog(pending, false);
            }
          }, 1500);
          return;
        }

        this.armAnnouncementRetry();
      });
  }

  /**
   * Waits for the next navigation onto an allowed route and tries once more. `take(1)` is what
   * makes this a single retry rather than a standing listener that fires on every navigation.
   */
  private armAnnouncementRetry() {
    if (this.announcementRetryArmed) {
      return;
    }
    this.announcementRetryArmed = true;

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        filter(() => this.announcementGate.isOnAllowedRoute()),
        take(1),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => {
        this.announcementService.dialog$
          .pipe(take(1))
          .subscribe(pending => {
            if (pending?.length && !this.announcementDialogShown
              && this.announcementGate.canInterrupt()) {
              this.openAnnouncementDialog(pending, false);
            }
          });
      });
  }

  private openAnnouncementDialog(announcements: Announcement[], blocking: boolean) {
    this.announcementDialogShown = true;
    this.dialog.open(AnnouncementDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      autoFocus: false,
      // Only a critical notice blocks. Everything else closes on Esc and on the backdrop, which
      // the dialog records as "seen" rather than "dismissed" — a deliberate non-answer.
      disableClose: blocking,
      data: { announcements },
    });
  }

  private checkPendingTasksForToday() {
    // Doctors never get this popup, so their app does not load the practice's whole task list for it.
    this.userService.userProfile$
      .pipe(
        switchMap((user: any) => user && user.userType !== 'doctor'
          ? this.taskService.tasks$.pipe(map((tasks: any[] | null) => [tasks, user] as [any[] | null, any]))
          : EMPTY),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(async ([tasks, user]) => {
        // Re-read the dismissal on every emission. Checking it once before subscribing
        // meant that after the user dismissed the dialog the guard was never consulted
        // again, so the next tasks$ emission re-opened it in the same session.
        if (!tasks || localStorage.getItem(this.tasksDismissedKey) || this.dialogShown) {
          return;
        }
        if (user && user.userType !== 'doctor' && tasks.length > 0) {
          // tasks$ holds every active task for the group regardless of status
          // (loadTasksByGroup filters on active only), so the popup has to exclude
          // finished work itself — otherwise it fires on days where every task is done.
          // Treat a missing status as outstanding: legacy rows predate the status field.
          // Only the viewer's tasks: assigned to nobody (the whole practice) or to them
          // (`tasks/task-assignment.md`). Doctors never get this popup — a task assigned to a
          // doctor goes to that doctor's Inbox instead.
          const tasksForToday = tasks.filter((task: any) =>
            isTaskForUser(task, user.id) &&
            task.dueDate &&
            task.status !== 'Completed' &&
            task.status !== 'Cancelled' &&
            moment(task.dueDate).isSame(moment(), 'day'));
          if (tasksForToday.length > 0) {
            this.dialogShown = true;
            setTimeout(async () => {
              try {
                const taskCount = tasksForToday.length;
                const confirmed = await this.feedbackService.showConfirmation('Pending Tasks for Today', 'You have ' + taskCount + ' pending ' + (taskCount > 1 ? 'tasks' : 'task') + ' for today. Would you like to see the task list now?', [], 'Ignore', 'Take Me There');
                if (confirmed) {
                  this.router.navigate(['/patients/filters']);
                }
              } finally {
                // Recorded in finally so a failed dialog cannot leave the prompt
                // re-firing on every subsequent emission. Key is read fresh, so a
                // session running past midnight dismisses against the new day.
                localStorage.setItem(this.tasksDismissedKey, 'true');
                this.dialogShown = false;
              }
            }, 5000);
          }
        }
      });
  }

  private observeBreakpoints() {
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape, Breakpoints.TabletPortrait])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(result => {
        this.isMobile = result.matches;
      });
  }

  private subscribeToUserProfile() {
    this.userService.userProfile$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(user => {
        this.user = user;
        if (user && !user.isVerified) {
          this.showEmailVerificationRequired();
        }
      });
  }

  private showEmailVerificationRequired() {
    const ref = this.snackBar.openFromComponent(EmailVerificationSnackbarComponent, {
      duration: 60 * 1000,
      panelClass: ['themed-snackbar'],
    });

    ref.onAction()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.userService.resendVerificationEmail()
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(() => {
            this.snackBar.open(`Verification email was resent to ${this.user?.email}`, '', {
              duration: 3000,
              panelClass: ['themed-snackbar']
            });
          });
      });
  }

  private checkShouldEnableExperimentalFeatures() {
    this.groupService.selectedGroup$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(group => {
        if (group && environment.EXPERIMENTAL_FEATURE_GROUP_IDS.includes(group.id)) {
          localStorage.setItem('experimentalFeaturesEnabled', 'true');
        } else {
          localStorage.setItem('experimentalFeaturesEnabled', 'false');
        }
      });
  }

  /**
   * Sets up a beforeunload warning to prevent users from accidentally leaving the application.
   * This is especially useful on mobile devices where users might accidentally refresh while scrolling.
   */
  private setupBeforeUnloadWarning(): void {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // A deliberate sign-out is not an accident, so it must not be confirmed. This component is
      // still mounted when `UserService.logoutAndRedirect()` navigates — it no longer routes to
      // `/login` first, which is what used to unmount us and remove this listener in time — so
      // without the check every log-out would raise a browser "Leave site?" dialog.
      if (this.userService.isSigningOut) {
        return undefined;
      }

      // Always show warning for logged-in users to prevent accidental navigation/refresh
      const message = 'Are you sure you want to leave? Your progress may be lost.';
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    this.handleBeforeUnload = handleBeforeUnload;
  }

}
